<script setup lang="ts">
import {
  PRODUCT_STOCK_LOW_THRESHOLD,
  RESIZE_DELAY,
  SiteIdKey,
  STANDARD_DISPATCH_TIME,
} from '~/config/product-availability'

export type ProductAvailabilityProps = {
  productStoreLocation: ProductStoreLocation
  pdpDeliveryCallout?: boolean
  // My Bag & Checkout presentation.
  isCartStyle?: boolean
}
export type ProductAvailability = {
  icon?: string
  message?: string
  callout?: any // RichTextPanel Contentful Object
  onlineStockIndicator?: string
  showMTOCancellationMsg?: boolean
  madeToOrderInfo?: boolean
  dispatchLeadTime?: boolean
}
const props = defineProps<ProductAvailabilityProps>()
const emit = defineEmits<{
  'open-size-selector': []
}>()
const i18n = useI18n()
const preferenceStore = usePreferencesStore()
const productStore = useProductStore(props.productStoreLocation)
const { bopisEnabled } = useFeatureFlags()

// The BOPIS My Bag & Checkout treatment; markets without it keep the previous presentation.
const isBopisCart = computed(() => !!props.isCartStyle && bopisEnabled())

// My Bag & Checkout say "Ships in"; the PDP says "Ready in".
const shipInDaysKey = computed(() =>
  isBopisCart.value
    ? 'product.product_availability.availability_message_label.ships_in_days'
    : 'product.product_availability.availability_message_label.ship_in_days',
)
// REVIEW: added as a backup, but want to ask do we need this? looks like we are still initializing all pref stores but either way maybe good defensive coding
if (!preferenceStore.pdp) {
  preferenceStore.init(['pdpPreferences'])
}
const madeToOrderLeadTimeCallout = preferenceStore.pdp?.madeToOrderLeadTimeCallout
const dispatchAndProcessingLeadTimeCallout = preferenceStore.pdp?.dispatchAndProcessingLeadTimeCallout
const isPdp = props.productStoreLocation === 'pdp'

// SAFETY: every locale in localeConfig carries one of the three SiteIdKey values.
const siteId = localeConfig[i18n.locale.value]?.siteId as SiteIdKey
const productStockLowThreshold = PRODUCT_STOCK_LOW_THRESHOLD[siteId]
const standardDispatchTime = preferenceStore.site?.standardDispatchTime ?? STANDARD_DISPATCH_TIME[siteId]
const resizeDelay = preferenceStore.site?.resizeEngravingDelayTime ?? RESIZE_DELAY[siteId]

const leadTime = computed(() => productStore.selectedProduct?.c_specialOrderLeadTime || 0)
const isSOProduct = computed(() => productStore.selectedProduct?.c_mhj_isSpecialOrderProduct)
const lowStockMessage = props.pdpDeliveryCallout
  ? i18n.t('product.product_availability.availability_message_label.delivery_low_stock')
  : i18n.t('product.product_availability.availability_message_label.low_stock')

const availability = computed<ProductAvailability>(() => {
  const delay = standardDispatchTime + resizeDelay
  const isEngraved = productStore.engravingText

  // The product has custom attributes regarding ring sizes. We use the ring size attributes to determine availability
  if (productStore.selectedProcessedSizeOptions) {
    // All options with c_mhj_isSpecialOrder are special order-able
    if (productStore.selectedProcessedSizeOptions.isSpecialOrder) {
      return {
        icon: 'madeToOrder',
        message: i18n.t(shipInDaysKey.value, leadTime.value),
        callout: madeToOrderLeadTimeCallout,
        showMTOCancellationMsg: true,
      }
    }
    // All options that aren't in c_mhj_isSpecialOrderProduct that are inStock are order-able
    if (productStore.selectedProcessedSizeOptions.inStock) {
      return {
        ...(!isEngraved && {
          icon: 'ready',
        }),
        ...(isEngraved
          ? {
              icon: 'engraved',
              message: i18n.t(shipInDaysKey.value, delay),
              callout: dispatchAndProcessingLeadTimeCallout,
            }
          : {
              message: i18n.t('product.product_availability.availability_message_label.ready_to_ship'),
            }),
        onlineStockIndicator:
          productStore.selectedProcessedSizeOptions.stockLevel <= productStockLowThreshold
            ? lowStockMessage
            : i18n.t('product.product_availability.availability_message_label.in_stock'),
      }
    }
    // orders that are not in stock, but are resizable to requested size
    if (productStore.selectedProcessedSizeOptions?.resizeAvailable) {
      return {
        icon: 'resizedForYou',
        message: i18n.t(shipInDaysKey.value, delay),
        callout: dispatchAndProcessingLeadTimeCallout,
        onlineStockIndicator:
          (productStore.selectedProcessedSizeOptions.stockLevel ??
            productStore.productAvailabilityModel.inStockAmount) <= productStockLowThreshold
            ? lowStockMessage
            : i18n.t('product.product_availability.availability_message_label.in_stock'),
      }
    }
    // If none of the conditions apply, mark the product out of stock
    return {
      onlineStockIndicator: i18n.t('product.product_availability.availability_message_label.out_of_stock_online'),
    }
  }

  // Special Order (Made-to-order) check for single products that have no other variants.
  // Skip if processedSizeOptions already indicate an in-stock, non-special-order option exists.
  const hasInStockOption = productStore.selectedProduct?.c_mhj_processedSizeOptions?.some(
    (opt) => opt.inStock && !opt.isSpecialOrder,
  )
  if (isSOProduct.value && !hasInStockOption) {
    return {
      icon: 'madeToOrder',
      message: i18n.t(shipInDaysKey.value, leadTime.value),
      callout: madeToOrderLeadTimeCallout,
      showMTOCancellationMsg: true,
    }
  }

  // On non-size-variants use the productAvailabilityModel helper on to determine availability
  if (productStore.productAvailabilityModel?.isInStock || productStore.productAvailabilityModel?.isLowStock) {
    return {
      ...(!isEngraved && {
        icon: 'ready',
      }),
      ...(isEngraved
        ? {
            icon: 'engraved',
            message: i18n.t(shipInDaysKey.value, delay),
            callout: dispatchAndProcessingLeadTimeCallout,
          }
        : {
            message: i18n.t('product.product_availability.availability_message_label.ready_to_ship'),
          }),
      onlineStockIndicator:
        productStore.productAvailabilityModel.inStockAmount <= productStockLowThreshold
          ? lowStockMessage
          : i18n.t('product.product_availability.availability_message_label.in_stock'),
    }
  }
  if (productStore.productAvailabilityModel?.isLowStock) {
    return {
      onlineStockIndicator: lowStockMessage,
    }
  }
  return {
    onlineStockIndicator: i18n.t('product.product_availability.availability_message_label.out_of_stock_online'),
  }
})

const showAvailability = computed(() => {
  const pdpHasSpecialIcon =
    isPdp && (availability.value?.icon === 'madeToOrder' || availability.value?.icon === 'resizedForYou')

  const deliveryCalloutVisible =
    props.pdpDeliveryCallout &&
    (productStore.selectedProcessedSizeOptions?.inStock ||
      (!productStore.selectedProcessedSizeOptions &&
        (productStore.productAvailabilityModel?.isInStock || productStore.productAvailabilityModel?.isLowStock)))

  return !productStore.isMutliSizeProductWithoutSelectedSize && (pdpHasSpecialIcon || !isPdp || deliveryCalloutVisible)
})
</script>

<template>
  <div
    v-if="showAvailability"
    class="product-availability"
    data-testid="product-availability"
    :class="{
      'product-availability--pdp': isPdp && !pdpDeliveryCallout,
      'pdp-delivery-callout': pdpDeliveryCallout,
      'product-availability--cart': isBopisCart,
    }"
  >
    <ProductLeadTimeCallOut
      v-if="availability.message"
      :availability="availability"
      :is-pdp="isPdp"
      :pdp-delivery-callout="pdpDeliveryCallout"
      :is-cart-style="isCartStyle"
    />

    <!-- The BOPIS cart callout replaces the indicator; out of stock has no callout, so it stays. -->
    <i18n-t
      v-if="availability.onlineStockIndicator && !isPdp && !(isBopisCart && availability.message)"
      keypath="product.product_availability.online_availability"
      scope="global"
      tag="div"
      class="product-availability__online-stock-indicator-container"
      data-testid="product-availability__online-stock"
    >
      {{ $t('product.product_availability.online_availability') }}
      <template #availabilityStatus>
        {{ availability.onlineStockIndicator }}
      </template>
    </i18n-t>
    <div
      v-if="isPdp && !pdpDeliveryCallout && availability.icon === 'resizedForYou'"
      class="product-availability__resize-message"
    >
      {{ $t('product.product_availability.requires_resizing') }}
    </div>
  </div>
  <div v-else-if="pdpDeliveryCallout && !showAvailability" class="product-availability__no-size">
    <template
      v-if="
        availability.onlineStockIndicator ===
        i18n.t('product.product_availability.availability_message_label.out_of_stock_online')
      "
    >
      <strong class="product-availability__out-of-stock">{{
        $t('product.product_availability.availability_message_label.out_of_stock_online')
      }}</strong>
    </template>

    <template v-else>
      <div class="product-availability__select-size">
        <Button variant="text" @click="emit('open-size-selector')">{{ $t('product.selector.select_a_size') }}</Button>
        {{ $t('product.selector.see_stock') }}
      </div>
    </template>
  </div>
</template>

<style lang="postcss">
.product-availability:not(.pdp-delivery-callout) {
  background: var(--surface-light-20);
  padding: var(--space-40);
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  min-height: 3.7rem;
  position: relative;

  &:has(.product-lead-time__message-icon--mfy) {
    background: var(--surface-light-40);
  }

  .product-availability__online-stock-indicator-container {
    text-align: center;
    font-size: var(--text-xsmall);
  }

  .product-availability__resize-message {
    font-size: var(--text-xsmall);
  }
}
.product-availability--pdp:not(.pdp-delivery-callout) {
  background: var(--surface-light-40);
}

/* The reserved height is for the online stock indicator, which cart style doesn't render. */
.product-availability--cart:not(.pdp-delivery-callout) {
  min-height: 0;
}

.product-availability__low-stock {
  text-align: right;
  font-size: var(--text-small);
}

.product-availability__no-size {
  display: flex;
  justify-content: space-between;
  font-weight: bold;

  .product-availability__out-of-stock {
    color: var(--status-error);
  }
}
.product-availability__select-size {
  font-weight: 500;
}
</style>
